import { memo } from 'react';
import DOMPurify from 'dompurify';

interface SafeHTMLProps {
  html: string;
  className?: string;
  /**
   * Custom DOMPurify configuration
   * Default allows: links, basic formatting, line breaks
   * Blocks: scripts, event handlers, iframes
   */
  config?: DOMPurify.Config;
}

/**
 * SafeHTML Component
 *
 * Safely renders HTML content by sanitizing it with DOMPurify
 * to prevent XSS (Cross-Site Scripting) attacks.
 *
 * @example
 * ```tsx
 * // Before (UNSAFE)
 * <span dangerouslySetInnerHTML={{ __html: userContent }} />
 *
 * // After (SAFE)
 * <SafeHTML html={userContent} />
 * ```
 */
export const SafeHTML = memo(({ html, className, config }: SafeHTMLProps) => {
  // Default configuration: allow safe HTML, block dangerous elements
  const defaultConfig: DOMPurify.Config = {
    ALLOWED_TAGS: [
      'a',
      'b',
      'i',
      'strong',
      'em',
      'u',
      's',
      'br',
      'p',
      'span',
      'div',
      'ul',
      'ol',
      'li',
      'blockquote',
      'code',
      'pre',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  };

  // Merge custom config with defaults
  const sanitizeConfig: DOMPurify.Config = { ...defaultConfig, ...config };

  // Sanitize the HTML
  const sanitizedHTML = DOMPurify.sanitize(html, sanitizeConfig as any);

  return <span className={className} dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />;
});

SafeHTML.displayName = 'SafeHTML';
